import { useSignal } from '@preact/signals';
import { useEffect, useRef } from 'preact/hooks';

import type { GeoSuggestion } from '@amumin/api-client';
import {
  asrSchoolForMadhab,
  builtinFitsMethod,
  methodForMadhab,
  type ExtrasGet,
  type ExtrasView,
  type PrayerMethod,
  type PrayerSettings,
  type PrayerSettingsView,
} from '@amumin/domain';
import { interpolate } from '@amumin/i18n';
import { Button, Notice, RadioList, TextField, showToast } from '@amumin/ui';
import { Icon } from '@amumin/ui-core';

import { api, isApiError } from '../api.js';
import { ConsentBlock } from '../profile/Choices.js';
import { draftFromView, draftToPut, joinNames } from '../profile/model.js';
import { withMethod } from '../lib/extrasDraft.js';
import { toastError } from '../lib/ui.js';
import { appText, bumpData, dataVersion, household } from '../state/session.js';
import { wp } from '../weather/text.js';
import styles from './prayerQuestion.module.css';

// "Gebedstijden op het bord?" (oprichtersbesluit 2026-09-25: "bij de onboarding kiest een gezin de
// module wel of niet"; "kind hoort bij gezin"; DESIGN §8.P.2 en §8.GB.1). Eén rustige vraag, alleen
// als de API de module aanbiedt (Islam bij het gezin of bij een lid). Nooit vooraf gekozen.
// - Ja, graag: modulevlag aan (POST extras/answer), daarna in dezelfde stroom de plek (postcode of
//   plaats in Nederland; per tablet blijft in Instellingen > Locatie) en de voorgestelde methode
//   met één regel waarom, met een link om te wijzigen.
// - Nee, niet nu: de vlag blijft uit (het antwoord wordt bewaard), één regel waar het later kan.
// De kaart (Vandaag, Instellingen, Wat past bij ons gezin) laadt zelf; beantwoord = weg.

const MIN_CHARS = 3;
const DEBOUNCE_MS = 300;
const SETUP_METHODS: ReadonlyArray<PrayerMethod> = [
  'eu_joint',
  'diyanet',
  'mwl',
  'morocco',
  'uoif',
  'jafari',
];

type Naming = 'turkish' | 'arabic';

/** De vraag zelf: titel en twee regels uitleg in de gekozen namen (ezan/adhan, İmsak/Fajr). */
export function PrayerQuestionText({ naming, titleId }: { naming: Naming; titleId?: string }) {
  const p = wp.value.prayer;
  const q = p.question;
  return (
    <>
      <p class={`section-title ${styles.title}`} id={titleId}>
        {q.title}
      </p>
      <p class={styles.line}>{interpolate(q.body, { call: p.call[naming] })}</p>
      <p class={`muted ${styles.line}`}>{interpolate(q.body2, { fajr: p.names[naming].fajr })}</p>
    </>
  );
}

/** Na "Ja, graag": de plek (als die er nog niet is) en de voorgestelde methode. */
export function PrayerSetup({
  view,
  onView,
  onDone,
  doneLabel,
}: {
  view: PrayerSettingsView;
  onView: (v: ExtrasView) => void;
  onDone: () => void;
  doneLabel: string;
}) {
  const p = wp.value.prayer;
  const q = p.question;
  const s = view.settings;
  // In de zin met lidwoord ("We rekenen met de Europese gezamenlijke kalender"; golf 6c live r1).
  const method = p.methodInSentence[s.method];
  // Golf 6c: "omdat jullie de Hanafi-school volgen" alleen zolang de methode bij de mazhab ook
  // gekozen is (in Europa de gezamenlijke kalender van Diyanet en Milli Görüş). Nooit meer "omdat
  // jullie Turks koken": de methode volgt de mazhab, niet de keuken. Geen voorstel van de server
  // (die kent ook de breedte: boven 56° NB is het Diyanet) = de methode past bij de mazhab.
  const suggestion = view.method_suggestion;
  const byMadhab =
    view.method_reason === 'madhab_hanafi' &&
    methodForMadhab(view.madhab) !== null &&
    suggestion === null;
  // Wijzigen blijft in dezelfde stroom (review e6 r1): in de wizard verlaat het gezin anders de
  // wizard zonder weg terug. De hele pagina Gebedstijden blijft voor de rest (Instellingen).
  const changing = useSignal(false);
  const saving = useSignal(false);
  const displaced = useRef<PrayerSettings['recording'] | null>(null);
  const choose = async (m: PrayerMethod) => {
    if (saving.value || m === s.method) return;
    saving.value = true;
    try {
      // Zelfde regel als de pagina Gebedstijden: naar Jafari wordt een ingebouwde opname het
      // belletje; terug brengt de opname die Jafari opzij zette.
      if (s.recording.kind === 'builtin' && !builtinFitsMethod(m)) displaced.current = s.recording;
      const moved = withMethod(s, m, view.location?.country_code ?? null, {
        available: view.recordings.filter((r) => r.id !== 'tone' && r.available).map((r) => r.id),
        remembered: displaced.current,
      });
      // Golf 6c: volgt Asr de mazhab, dan rekent hij mee met de methode (de server doet hetzelfde).
      const next =
        moved.asr_school_source === 'madhab'
          ? { ...moved, asr_school: asrSchoolForMadhab(view.madhab, m) }
          : moved;
      const { version, offsets_confirmed_at: _confirmed, ...rest } = next;
      onView(
        await api.app.extras.put<ExtrasView>({
          prayer: { ...rest, enabled: true, if_version: version },
        }),
      );
      changing.value = false;
    } catch (e) {
      toastError(e);
      if (isApiError(e, 409)) {
        void api.app.extras
          .status<ExtrasView>()
          .then(onView)
          .catch(() => undefined);
      }
    } finally {
      saving.value = false;
    }
  };
  return (
    <div class={styles.setup} data-name="prayer-setup">
      {view.location ? (
        <p class={styles.place} data-name="prayer-setup-place">
          <Icon id="map-pin" size={18} />
          <span>{interpolate(q.placeChosen, { label: view.location.label })}</span>
        </p>
      ) : (
        <PlaceSearch onChosen={onView} />
      )}
      <p class={styles.line} data-name="prayer-setup-method" data-method={s.method}>
        {interpolate(byMadhab ? q.methodMadhab : q.methodDefault, { method })}{' '}
        {changing.value ? null : (
          <Button name="prayer-setup-change" variant="link" onPress={() => (changing.value = true)}>
            {q.change}
          </Button>
        )}
      </p>
      {suggestion && !changing.value ? (
        // Golf 6c: de kalender die bij de mazhab past, als voorstel met één tik (nooit stil).
        <Notice kind="info" name="prayer-setup-suggestion">
          <span>
            {interpolate(p.madhabSuggestion.body, { method: p.methodInSentence[suggestion] })}{' '}
          </span>
          <Button
            name="prayer-setup-suggestion-apply"
            variant="link"
            busy={saving.value}
            onPress={() => void choose(suggestion)}
          >
            {p.madhabSuggestion.apply}
          </Button>
        </Notice>
      ) : null}
      {changing.value ? (
        <div data-name="prayer-setup-methods" aria-busy={saving.value}>
          <RadioList
            name="prayer-setup-methods"
            label={q.methodChoose}
            value={s.method}
            options={SETUP_METHODS.map((m) => ({
              value: m,
              label: p.method[m],
              hint: p.methodHint[m],
            }))}
            onChange={(m) => void choose(m)}
          />
        </div>
      ) : null}
      {view.today ? (
        <p class="muted small" data-name="prayer-setup-enabled">
          {q.enabled}
        </p>
      ) : null}
      <div class={styles.actions}>
        <Button
          name="prayer-setup-done"
          variant="primary"
          icon="check"
          disabled={!view.location}
          onPress={onDone}
        >
          {doneLabel}
        </Button>
      </div>
    </div>
  );
}

/** Postcode of plaats in Nederland (zelfde zoekroute als Instellingen > Locatie). */
function PlaceSearch({ onChosen }: { onChosen: (v: ExtrasView) => void }) {
  const g = wp.value.geo;
  const q = wp.value.prayer.question;
  const query = useSignal('');
  const results = useSignal<GeoSuggestion[] | null>(null);
  const chosen = useSignal<string>('');
  const searching = useSignal(false);
  const saving = useSignal(false);
  const problem = useSignal<string | null>(null);
  const timer = useRef<number | null>(null);
  const seq = useRef(0);
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );
  const run = async (text: string) => {
    const mine = (seq.current += 1);
    searching.value = true;
    problem.value = null;
    try {
      const out = await api.weather.geoSearch(text, 'NL');
      if (mine !== seq.current) return;
      results.value = out.suggestions;
    } catch (e) {
      if (mine !== seq.current) return;
      results.value = null;
      if (isApiError(e, 429)) problem.value = g.tooMany;
      else if (isApiError(e, 503)) problem.value = g.web.unavailable;
      else toastError(e);
    } finally {
      if (mine === seq.current) searching.value = false;
    }
  };
  const onQuery = (value: string) => {
    query.value = value;
    chosen.value = '';
    if (timer.current !== null) window.clearTimeout(timer.current);
    if (value.trim().length < MIN_CHARS) {
      results.value = null;
      return;
    }
    timer.current = window.setTimeout(() => void run(value.trim()), DEBOUNCE_MS);
  };
  const save = async () => {
    if (!chosen.value || saving.value) return;
    saving.value = true;
    try {
      const geo = await api.weather.geo();
      await api.weather.geoChoose(chosen.value, geo.household?.version ?? null);
      onChosen(await api.app.extras.status<ExtrasView>());
    } catch (e) {
      toastError(e);
    } finally {
      saving.value = false;
    }
  };
  return (
    <div class={styles.placeSearch} data-name="prayer-place">
      <p class={`section-title ${styles.title}`}>{q.placeTitle}</p>
      <p class={`muted ${styles.line}`}>{q.placeBody}</p>
      <TextField
        id="prayer-place-query"
        name="prayer-place-query"
        label={g.searchLabel}
        value={query.value}
        onInput={onQuery}
        placeholder={g.searchPlaceholder}
        autoComplete="off"
        maxLength={80}
      />
      {problem.value ? (
        <Notice kind="warning" name="prayer-place-problem">
          {problem.value}
        </Notice>
      ) : null}
      {searching.value ? <p class="muted small">{g.web.searching}</p> : null}
      {results.value && results.value.length === 0 && !searching.value ? (
        <p class="muted" data-name="prayer-place-none">
          {g.noResults}
        </p>
      ) : null}
      {results.value && results.value.length > 0 ? (
        <>
          <RadioList
            name="prayer-place-suggestion"
            label={g.web.pick}
            value={chosen.value}
            onChange={(id) => (chosen.value = id)}
            options={results.value.map((s) => ({ value: s.id, label: s.label }))}
          />
          <p class="muted small">{g.credits.pdok}</p>
        </>
      ) : null}
      <div class={styles.actions}>
        <Button
          name="prayer-place-save"
          icon="map-pin"
          busy={saving.value}
          disabled={!chosen.value}
          onPress={() => void save()}
        >
          {q.placeSave}
        </Button>
      </div>
      <p class="muted small">{q.placeOther}</p>
    </div>
  );
}

/**
 * Oudere toestemmingstekst (noemt de extra onderdelen niet): eerst opnieuw "Mag dit?" met de
 * huidige tekst (vervangen, niet wissen; DESIGN §8.P.3). Het profiel zelf blijft gelijk.
 */
async function renewConsent(): Promise<void> {
  const v = await api.app.profile.get();
  const locale = (household.value?.locale ?? 'nl-NL').startsWith('en') ? 'en' : 'nl';
  await api.app.profile.save(draftToPut(draftFromView(v), v.version, locale));
}

type Phase = 'ask' | 'renew' | 'setup' | 'declined' | 'gone';

/**
 * De vraag als kaart (Vandaag, Instellingen > Gebedstijden, Wat past bij ons gezin, en de pagina
 * Gebedstijden zolang er geen antwoord is). Toont niets zonder aanbod of als het gezin al koos.
 */
export function PrayerQuestionCard({
  name,
  because,
  onFinished,
  banner = false,
}: {
  /** Waar de kaart staat (data-name `prayer-question-<name>`). */
  name: string;
  /**
   * Namen van de leden door wie de vraag er is, als het gezin zelf geen Islam koos (kind hoort bij
   * gezin; review e6 r1): één regel "Omdat Yusuf Islam volgt.". Leeg of weg: geen regel.
   */
  because?: ReadonlyArray<string>;
  /** Na het antwoord (en bij ja na de plek): bijvoorbeeld de pagina opnieuw laden. */
  onFinished?: () => void;
  /**
   * UX-ronde 2 (Vandaag, Instellingen, Wat past bij ons gezin): de vraag als smalle banner met "Ja,
   * graag" en een kruisje ("Nee, niet nu"). Na "Ja, graag" wordt het de gewone kaart (de plek en de
   * methode kiezen); na "Nee" blijft één stille regel over die zegt waar het later kan.
   */
  banner?: boolean;
}) {
  const a = appText.value;
  const q = wp.value.prayer.question;
  const view = useSignal<PrayerSettingsView | null>(null);
  const phase = useSignal<Phase>('ask');
  const busy = useSignal<'yes' | 'no' | null>(null);
  const consent = useSignal(false);

  useEffect(() => {
    api.app.extras
      .get<ExtrasGet>()
      .then((v) => {
        // Een lopende stroom (plek kiezen) houdt zijn eigen stand.
        if (phase.value === 'ask') view.value = v.prayer ?? null;
      })
      .catch(() => {
        view.value = null;
      });
  }, [household.value?.id, dataVersion.value]);

  const v = view.value;
  if (phase.value === 'gone' || !v) return null;
  if (phase.value === 'ask' && v.configured) return null;

  const finish = () => {
    phase.value = 'gone';
    bumpData();
    onFinished?.();
  };

  const answer = async (which: 'yes' | 'no') => {
    if (busy.value) return;
    if (which === 'yes' && v.consent_renewal_required && phase.value !== 'renew') {
      phase.value = 'renew';
      return;
    }
    busy.value = which;
    try {
      if (which === 'yes' && phase.value === 'renew') await renewConsent();
      const next = await api.app.extras.answer<ExtrasView>(which);
      view.value = next.prayer;
      if (which === 'no') {
        phase.value = 'declined';
        bumpData();
      } else {
        phase.value = 'setup';
        showToast({ kind: 'success', message: wp.value.prayer.web.saved });
      }
    } catch (e) {
      if (isApiError(e, 409, 'consent_renewal_required')) phase.value = 'renew';
      else toastError(e);
    } finally {
      busy.value = null;
    }
  };

  // Banner na "Nee": één stille regel waar het later kan (geen kaart meer).
  if (banner && phase.value === 'declined') {
    return (
      <p
        class="status-line status-line-quiet"
        data-name={`prayer-question-${name}`}
        data-phase="declined"
        role="status"
      >
        <span class="status-line-icon" aria-hidden="true">
          <Icon id="moon" size={18} />
        </span>
        <span class="status-line-text" data-name="prayer-declined">
          {q.declined}
        </span>
      </p>
    );
  }

  if (banner && phase.value === 'ask') {
    const naming = v.settings.naming;
    const p = wp.value.prayer;
    return (
      <section
        class="inline-banner"
        aria-labelledby={`prayer-question-${name}-title`}
        data-name={`prayer-question-${name}`}
        data-phase="ask"
        data-variant="banner"
      >
        <span class="inline-banner-icon" aria-hidden="true">
          <Icon id="moon" size={18} />
        </span>
        <div class="inline-banner-text">
          <p class="inline-banner-title" id={`prayer-question-${name}-title`}>
            {q.title}
          </p>
          <p class="inline-banner-body">
            {interpolate(q.body, { call: p.call[naming] })}{' '}
            {interpolate(q.body2, { fajr: p.names[naming].fajr })}
          </p>
          {because && because.length > 0 ? (
            <p class="inline-banner-body" data-name="prayer-question-because">
              {because.length === 1
                ? interpolate(q.becauseOne, { name: because[0] ?? '' })
                : interpolate(q.becauseMany, {
                    names: joinNames(because, a.profile.warning.and),
                  })}
            </p>
          ) : null}
        </div>
        <div class="inline-banner-actions">
          <Button
            name="prayer-question-yes"
            variant="primary"
            size="small"
            busy={busy.value === 'yes'}
            onPress={() => void answer('yes')}
          >
            {q.yes}
          </Button>
        </div>
        <span class="inline-banner-close">
          <Button
            name="prayer-question-no"
            variant="ghost"
            icon="x"
            label={q.no}
            busy={busy.value === 'no'}
            onPress={() => void answer('no')}
          />
        </span>
      </section>
    );
  }

  return (
    <section
      class={`panel ${styles.card}`}
      aria-labelledby={`prayer-question-${name}-title`}
      data-name={`prayer-question-${name}`}
      data-phase={phase.value}
    >
      <span class={styles.art} aria-hidden="true">
        <Icon id="moon" size={28} />
      </span>
      <div class={styles.body}>
        {phase.value === 'setup' ? (
          <>
            <p class={`section-title ${styles.title}`} id={`prayer-question-${name}-title`}>
              {wp.value.prayer.title}
            </p>
            <PrayerSetup
              view={v}
              onView={(next) => (view.value = next.prayer)}
              onDone={finish}
              doneLabel={q.done}
            />
          </>
        ) : phase.value === 'declined' ? (
          <p class={styles.line} id={`prayer-question-${name}-title`} data-name="prayer-declined">
            {q.declined}
          </p>
        ) : (
          <>
            <PrayerQuestionText
              naming={v.settings.naming}
              titleId={`prayer-question-${name}-title`}
            />
            {because && because.length > 0 ? (
              <p class={`muted ${styles.line}`} data-name="prayer-question-because">
                {because.length === 1
                  ? interpolate(q.becauseOne, { name: because[0] ?? '' })
                  : interpolate(q.becauseMany, {
                      names: joinNames(because, a.profile.warning.and),
                    })}
              </p>
            ) : null}
            {phase.value === 'renew' ? (
              <ConsentBlock
                checked={consent.value}
                onChange={(c) => (consent.value = c)}
                renew
                a={a}
              />
            ) : null}
            <div class={styles.actions}>
              <Button
                name="prayer-question-yes"
                variant="primary"
                busy={busy.value === 'yes'}
                disabled={phase.value === 'renew' && !consent.value}
                onPress={() => void answer('yes')}
              >
                {phase.value === 'renew' ? q.renew : q.yes}
              </Button>
              <Button
                name="prayer-question-no"
                variant="ghost"
                busy={busy.value === 'no'}
                onPress={() => void answer('no')}
              >
                {q.no}
              </Button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/**
 * Onderaan de pagina Gebedstijden zolang de module aan staat (review e6 r2): rustig weer uitzetten,
 * met één korte bevestiging. Het is hetzelfde antwoord "nee" als op de kaart; de instellingen blijven
 * bewaard, de pagina toont daarna PrayerOffPanel en de kiosk laat de gebedsregel bij zijn volgende
 * sync weg.
 */
export function PrayerTurnOff({ onView }: { onView: (v: ExtrasView) => void }) {
  const p = wp.value.prayer;
  const q = p.question;
  const asking = useSignal(false);
  const busy = useSignal(false);
  const turnOff = async () => {
    if (busy.value) return;
    busy.value = true;
    try {
      onView(await api.app.extras.answer<ExtrasView>('no'));
      bumpData();
    } catch (e) {
      toastError(e);
    } finally {
      busy.value = false;
    }
  };
  return (
    <section
      class="section panel"
      aria-labelledby="extras-turn-off-title"
      data-name="extras-turn-off"
    >
      <h2 class="section-title" id="extras-turn-off-title">
        {p.enabled}
      </h2>
      {asking.value ? (
        <>
          <p data-name="extras-turn-off-confirm">{q.turnOffConfirm}</p>
          <div class={styles.actions}>
            <Button
              name="extras-turn-off-yes"
              variant="primary"
              busy={busy.value}
              onPress={() => void turnOff()}
            >
              {q.turnOffYes}
            </Button>
            <Button
              name="extras-turn-off-cancel"
              variant="ghost"
              onPress={() => (asking.value = false)}
            >
              {q.turnOffCancel}
            </Button>
          </div>
        </>
      ) : (
        <>
          <p class="muted">{q.offBody}</p>
          <div class={styles.actions}>
            <Button
              name="extras-turn-off-start"
              variant="ghost"
              onPress={() => (asking.value = true)}
            >
              {q.turnOff}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}

/**
 * Module aan, maar met een oudere toestemmingstekst (review e6 r2): de toestemming meteen hier geven
 * in plaats van een doodlopende melding. Uitzetten kan ook zonder nieuwe toestemming.
 */
export function PrayerRenewPanel({ onView }: { onView: (v: ExtrasView) => void }) {
  const a = appText.value;
  const p = wp.value.prayer;
  const busy = useSignal(false);
  const consent = useSignal(false);
  const give = async () => {
    if (busy.value) return;
    busy.value = true;
    try {
      await renewConsent();
      const next = await api.app.extras.get<ExtrasGet>();
      if (next.prayer) onView(next as ExtrasView);
      bumpData();
    } catch (e) {
      toastError(e);
    } finally {
      busy.value = false;
    }
  };
  return (
    <section class="section panel" data-name="extras-consent-renewal">
      <p>{p.question.renewBody}</p>
      <ConsentBlock checked={consent.value} onChange={(c) => (consent.value = c)} renew a={a} />
      <div class={styles.actions}>
        <Button
          name="extras-renew"
          variant="primary"
          busy={busy.value}
          disabled={!consent.value}
          onPress={() => void give()}
        >
          {p.question.renewOnly}
        </Button>
      </div>
    </section>
  );
}

/** Pagina Gebedstijden met de vlag uit ("Nee, niet nu" eerder): één knop om aan te zetten. */
export function PrayerOffPanel({
  renewalRequired,
  onView,
}: {
  /** Oudere toestemmingstekst: aanzetten vraagt eerst hier opnieuw "Mag dit?" (review e6 r1). */
  renewalRequired: boolean;
  onView: (v: ExtrasView) => void;
}) {
  const a = appText.value;
  const q = wp.value.prayer.question;
  const busy = useSignal(false);
  const renew = useSignal(false);
  const consent = useSignal(false);
  const turnOn = async () => {
    if (busy.value) return;
    // Zelfde stroom als de kaart op Vandaag: de toestemming meteen hier, niet via een andere pagina.
    if (renewalRequired && !renew.value) {
      renew.value = true;
      return;
    }
    busy.value = true;
    try {
      if (renew.value) await renewConsent();
      onView(await api.app.extras.answer<ExtrasView>('yes'));
      bumpData();
    } catch (e) {
      if (isApiError(e, 409, 'consent_renewal_required')) renew.value = true;
      else toastError(e);
    } finally {
      busy.value = false;
    }
  };
  return (
    <section class="section panel" data-name="extras-off">
      <p>{q.off}</p>
      {renew.value ? (
        <div data-name="extras-consent-renewal">
          <ConsentBlock checked={consent.value} onChange={(c) => (consent.value = c)} renew a={a} />
        </div>
      ) : null}
      <div class={styles.actions}>
        <Button
          name="extras-turn-on"
          variant="primary"
          busy={busy.value}
          disabled={renew.value && !consent.value}
          onPress={() => void turnOn()}
        >
          {renew.value ? q.renew : q.turnOn}
        </Button>
      </div>
    </section>
  );
}
